import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IPartner } from '../../../model/partner';
import { fhwSliceActions } from '../../../store/modules/fhw/reducer';
import { distanceRadiusInKmSelector } from '../../../store/modules/filters/selectors';
import { mapSliceActions } from '../../../store/modules/map/reducer';
import { mapCentreSelector, mapSettingsSelector, mapZoomSelector } from '../../../store/modules/map/selectors';
import { usePartnersData } from '../../../store/modules/services/searchPartnersApi/hooks';
import { uiSliceActions } from '../../../store/modules/ui/reducer';
import { IMap, GoogleLatLng, GoogleMap, GoogleMarker } from './types';

import { MapUtils } from './utils';

// This can be used in case we want to set the headquarters as the center of the map
// const homeLocation = new google.maps.LatLng(mapSettings.defaultCentre.lat, mapSettings.defaultCentre.lng);
// MapUtils.addHomeMarker(homeLocation, map);

const Map: React.FC<IMap> = ({ mapType }: IMap): JSX.Element => {
	const dispatch = useDispatch();

	/** The native DOM element for the map */
	const bounds = useRef<google.maps.LatLngBounds>(new google.maps.LatLngBounds());
	/** The native DOM element for the map */
	const mapRef = useRef<HTMLDivElement>(null);
	/** The map in local state */
	const [map, setMap] = useState<GoogleMap>();
	/** A list of google map markers */
	const googleMarkers = useRef<GoogleMarker[]>([]);
	/** An array of map marker listener IDs (for marker clicks) */
	const listenerIdArray = useRef<any[]>([]);

	const infoWindow = useRef<google.maps.InfoWindow>(new google.maps.InfoWindow());

	/** Partners data */
	const data = usePartnersData();

	const mapCentre = useSelector(mapCentreSelector);
	const mapZoom = useSelector(mapZoomSelector);
	const mapSettings = useSelector(mapSettingsSelector);
	const distanceInKm = useSelector(distanceRadiusInKmSelector);

	/**
	 * Add a marker to the map, register a click listener and remember the marker listener ID
	 */
	const addMarker = useCallback(
		(location: GoogleLatLng, partner: IPartner): void => {
			if (map) {
				const marker = MapUtils.addMapMarker(location, map, partner, infoWindow.current);

				googleMarkers.current.push(marker);

				const listenerId = marker.addListener('click', function (event: google.maps.MapMouseEvent) {
					const partnerId = (this as any).partnerId;

					const markerPosition = {
						lat: event.latLng.lat(),
						lng: event.latLng.lng(),
					};

					
					dispatch(fhwSliceActions.setFocussedPartnerId(partnerId));
					dispatch(mapSliceActions.setMapCentre(markerPosition));
					dispatch(uiSliceActions.setIsPartnerListDrawerOpen({ isOpen: true }));
					dispatch(fhwSliceActions.setSelectedPartner(`${partnerId}`));
					dispatch(uiSliceActions.setIsIndividualProfileDrawerOpen({ isOpen: true }));

					// update the store with the current selected marker
					dispatch(
						mapSliceActions.setCurrentMarker({
							label: partnerId,
							...markerPosition,
						}),
					);

					// scroll to partners list item
					map.panTo(event.latLng);
					map.setZoom(mapSettings.defaultZoom);
				});

				listenerIdArray.current.push(listenerId);
			}
		},
		[dispatch, googleMarkers, listenerIdArray, map, mapSettings.defaultZoom],
	);

	const goToFirstItem = () => {
		const firstMarker = googleMarkers.current[0] as any;

		if (firstMarker) {
			const partnerId = firstMarker.partnerId;

			const markerPosition = {
				lat: (googleMarkers.current[0] as any).position.lat(),
				lng: (googleMarkers.current[0] as any).position.lng(),
			};

			dispatch(fhwSliceActions.setFocussedPartnerId(partnerId));
			dispatch(mapSliceActions.setMapCentre(markerPosition));
			dispatch(uiSliceActions.setIsPartnerListDrawerOpen({ isOpen: true }));

			dispatch(
				mapSliceActions.setCurrentMarker({
					label: partnerId,
					...markerPosition,
				}),
			);
		}
	};

	const removeMarkersFromMap = useCallback(() => {
		if (googleMarkers.current.length) {
			MapUtils.removeMapMarkers(googleMarkers.current);
			googleMarkers.current = [];
		}
		if (listenerIdArray.current.length) {
			listenerIdArray.current.forEach((listenerId) => {
				google.maps.event.removeListener(listenerId);
			});
		}
	}, []);

	const addMarkersToMap = useCallback(() => {
		data.partners.forEach((place) => {
			const location = new google.maps.LatLng(place.address.lat, place.address.lng);
			addMarker(location, place);
		});
		googleMarkers.current.forEach((marker) => marker && bounds.current.extend(marker.getPosition() as google.maps.LatLng));
		goToFirstItem();
		map?.fitBounds(bounds.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addMarker, data]);

	useEffect(() => {
		if (data?.partners && map) {
			removeMarkersFromMap();
			addMarkersToMap();
			dispatch(mapSliceActions.setMapZoom(12));
		}
	}, [addMarkersToMap, data, dispatch, map, removeMarkersFromMap]);

	/**
	 * Move the map to the defined centre when mapCentre Change
	 */
	useEffect(() => {
		if (map && mapCentre) {
			map.panTo({ lat: mapCentre.lat, lng: mapCentre.lng });
		}
	}, [map, mapCentre]);

	/**
	 * Zoom on the map when mapZoom changes
	 */
	useEffect(() => {
		if (map && mapZoom) {
			map.setZoom(mapZoom);
		}
	}, [map, mapZoom]);

	/**
	 * Update zoom map to fit the partners at the required radius
	 */
	useEffect(() => {
		if (map && data) {
			const bounds = new google.maps.LatLngBounds();
			data?.partners.forEach((partner) => {
				const latLng = new google.maps.LatLng(partner.address.lat, partner.address.lng);
				bounds.extend(latLng);
			});
			map.fitBounds(bounds);
		}
	}, [map, distanceInKm, data]);

	/**
	 * Init Map
	 */
	useEffect(() => {
		const defaultAddress = new google.maps.LatLng(mapSettings.defaultCenter.lat, mapSettings.defaultCenter.lng);
		if (mapRef.current) {
			setMap(
				MapUtils.createMap({
					mapRef,
					mapType,
					centerAddress: defaultAddress,
					zoomLevel: mapSettings.defaultZoom,
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * This event is in charge of updating the zoomMap level on the store if it changes via zoom control on maps
	 */
	useEffect(() => {
		if (map) {
			google.maps.event.addListener(map, 'zoom_changed', function () {
				if (map.getZoom() > mapZoom) {
					dispatch(mapSliceActions.setMapZoom(map.getZoom()));
				}
			});
		}

		return () => {
			if (map) google.maps.event.clearListeners(map, 'zoom_changed');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, map]);

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				padding: '0',
				height: '100%',
			}}>
			<div
				ref={mapRef}
				style={{
					width: '100%',
				}}></div>
		</div>
	);
};

export default memo(Map);
